import React from 'react';
import { ArrowRight, Zap, Shield, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

const differentiators = [
  {
    icon: Zap,
    title: 'Data-First Approach',
    description: 'Every decision backed by real-time analytics and consumer intelligence. No guesswork, only growth.',
  },
  {
    icon: Shield,
    title: 'Your Team, Extended',
    description: 'We embed within your workflow as a seamless extension — not an outside vendor, but a committed partner.',
  },
  {
    icon: BarChart3,
    title: 'Measurable Outcomes',
    description: 'If it can\'t be measured, we don\'t do it. Full transparency with attribution tracking across every channel.',
  },
];

const WhyGrouth = () => {
  return (
    <section className="bg-[#242326] py-16 md:py-24 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#83BF42]/5 rounded-full blur-[120px]" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#83BF42] text-sm uppercase tracking-[0.2em] mb-4 font-['Inter'] font-medium">
              Why Grouth
            </p>
            <h2 className="text-white text-3xl md:text-4xl lg:text-[48px] font-light leading-[1.15] tracking-tight font-['Inter']">
              Growth isn't luck.<br />It's engineered.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-white/60 text-lg font-['Lato'] font-light leading-relaxed">
              We combine deep market insights, cross-channel expertise, and cutting-edge adtech to build growth systems that compound. Your ambition is our blueprint.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {differentiators.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group p-6 md:p-8 rounded-2xl border border-white/8 hover:border-[#83BF42]/20 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-[#83BF42]/10 flex items-center justify-center mb-5 group-hover:bg-[#83BF42]/20 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-[#83BF42]" />
                </div>
                <h3 className="text-white text-lg font-medium font-['Inter'] mb-2">
                  {item.title}
                </h3>
                <p className="text-white/50 text-sm font-['Lato'] font-light leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Resource CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-14 p-8 md:p-10 rounded-2xl border border-white/8 bg-white/[0.02] flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div>
            <h3 className="text-white text-xl md:text-2xl font-medium font-['Inter'] mb-2">
              Get our Growth Playbook
            </h3>
            <p className="text-white/50 text-sm font-['Lato'] font-light max-w-lg">
              A tactical guide to scaling brand presence across digital channels. Strategies we've used to drive 340%+ sign-up growth for our clients.
            </p>
          </div>
          <button
            onClick={() => {
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#83BF42] text-[#83BF42] text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] hover:text-white transition-all duration-300 group"
          >
            Request Access
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyGrouth;
